/**
 * Webinar catalog client (GET /api/v1/webinars, docs/contract.md CR-24): the control service
 * answers with one composed view over the calendar schedule (webinar-kind rooms only),
 * the live room presence and the per-room recording archive. Authentication is the SPA's SSO
 * session bearer - the same token the calendar and cross-room recordings calls carry; guests
 * (room JWT bearers) cannot read it. The server sorts: live rows first, then upcoming by
 * start, then past newest first.
 */

/** One scheduled webinar event of a catalog row (null on ad-hoc rows). */
export interface WebinarEvent {
  readonly id: number;
  readonly title: string;
  /** RFC 3339; the occurrence window for recurring series. */
  readonly startsAt: string;
  readonly endsAt: string;
  readonly roomSlug: string;
  /** The long-form body text (markdown-ish plain text); null when the event carries none. */
  readonly description: string | null;
  /** The registration cap; null = unlimited (CR-27). */
  readonly capacity: number | null;
}

/** The viewer's own state in a catalog row's registration section (CR-27). */
export type WebinarMe = 'registered' | 'not_registered';

/** The registration section of a catalog row (CR-27): roster size, cap, the viewer's state. */
export interface WebinarRegistration {
  readonly registered: number;
  readonly capacity: number | null;
  readonly me: WebinarMe;
}

/** The resolved webinar room of a catalog row. */
export interface WebinarRoom {
  readonly slug: string;
  /** Always "webinar" in v1 - only webinar-kind rooms compose the catalog. */
  readonly kind: string;
  /** The room's lobby option: joining lands non-staff in the lobby. */
  readonly lobby: boolean;
}

/** Live presence of a catalog row (null when nobody is in the room right now). */
export interface WebinarLive {
  readonly active: boolean;
  readonly participants: number;
}

/** The archive section of a catalog row (null when the room has no completed recording). */
export interface WebinarRecordings {
  readonly count: number;
  /** RFC 3339 of the newest completion; null when absent. */
  readonly latestCompletedAt: string | null;
}

/** One catalog row. */
export interface WebinarEntry {
  readonly event: WebinarEvent | null;
  readonly room: WebinarRoom;
  readonly live: WebinarLive | null;
  readonly recordings: WebinarRecordings | null;
  /** Present exactly on calendar rows (CR-27); null on ad-hoc rows and when the calendar
   * module is disabled (the section is omitted wholesale). */
  readonly registration: WebinarRegistration | null;
  readonly source: 'calendar' | 'adHoc';
}

/** Error of the webinar catalog calls, carrying the contract envelope fields. */
export class WebinarsApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'WebinarsApiError';
    this.status = status;
    this.code = code;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Narrows the event section; null passes through, a malformed object fails the row. */
export function parseWebinarEvent(value: unknown): WebinarEvent | null {
  if (value === null) {
    return null;
  }
  if (!isRecord(value)) {
    throw new TypeError('webinar event must be an object or null');
  }
  const id =
    typeof value.id === 'number' && Number.isInteger(value.id) && value.id >= 0 ? value.id : null;
  const title = typeof value.title === 'string' && value.title.length > 0 ? value.title : null;
  const startsAt =
    typeof value.startsAt === 'string' && value.startsAt.length > 0 ? value.startsAt : null;
  const endsAt = typeof value.endsAt === 'string' && value.endsAt.length > 0 ? value.endsAt : null;
  const roomSlug =
    typeof value.roomSlug === 'string' && value.roomSlug.length > 0 ? value.roomSlug : null;
  if (id === null || title === null || startsAt === null || endsAt === null || roomSlug === null) {
    throw new TypeError('webinar event is missing a required field');
  }
  return {
    id,
    title,
    startsAt,
    endsAt,
    roomSlug,
    description: nullableString(value.description),
    capacity: nullableCount(value.capacity),
  };
}

/** Nullable string: null/absent passes as null, anything but a string fails. */
function nullableString(value: unknown): string | null {
  if (value === undefined || value === null) {
    return null;
  }
  if (typeof value !== 'string') {
    throw new TypeError('expected a string or null');
  }
  return value;
}

/** Nullable integer: null/absent passes as null, anything but a non-negative integer fails. */
function nullableCount(value: unknown): number | null {
  if (value === undefined || value === null) {
    return null;
  }
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 0) {
    throw new TypeError('expected a non-negative integer or null');
  }
  return value;
}

/** Narrows the registration section; null/absent passes through (ad-hoc rows, module off). */
export function parseWebinarRegistration(value: unknown): WebinarRegistration | null {
  if (value === undefined || value === null) {
    return null;
  }
  if (!isRecord(value)) {
    throw new TypeError('webinar registration must be an object or null');
  }
  const registered = nullableCount(value.registered);
  if (registered === null) {
    throw new TypeError('webinar registration is missing registered');
  }
  if (value.me !== 'registered' && value.me !== 'not_registered') {
    throw new TypeError('webinar registration me must be "registered" or "not_registered"');
  }
  return { registered, capacity: nullableCount(value.capacity), me: value.me };
}

/** Narrows the room section; a row without it is invalid. */
export function parseWebinarRoom(value: unknown): WebinarRoom {
  if (!isRecord(value)) {
    throw new TypeError('webinar room must be an object');
  }
  const slug = typeof value.slug === 'string' && value.slug.length > 0 ? value.slug : null;
  const kind = typeof value.kind === 'string' && value.kind.length > 0 ? value.kind : null;
  if (slug === null || kind === null || typeof value.lobby !== 'boolean') {
    throw new TypeError('webinar room is missing a required field');
  }
  return { slug, kind, lobby: value.lobby };
}

/** Narrows the live section; null passes through, a malformed object fails the row. */
export function parseWebinarLive(value: unknown): WebinarLive | null {
  if (value === null) {
    return null;
  }
  if (!isRecord(value)) {
    throw new TypeError('webinar live must be an object or null');
  }
  if (typeof value.active !== 'boolean' || typeof value.participants !== 'number') {
    throw new TypeError('webinar live is missing a required field');
  }
  return { active: value.active, participants: value.participants };
}

/** Narrows the recordings section; null passes through, a malformed object fails the row. */
export function parseWebinarRecordings(value: unknown): WebinarRecordings | null {
  if (value === null) {
    return null;
  }
  if (!isRecord(value)) {
    throw new TypeError('webinar recordings must be an object or null');
  }
  if (typeof value.count !== 'number') {
    throw new TypeError('webinar recordings is missing count');
  }
  let latestCompletedAt: string | null = null;
  if (value.latestCompletedAt !== undefined && value.latestCompletedAt !== null) {
    if (typeof value.latestCompletedAt !== 'string' || value.latestCompletedAt.length === 0) {
      throw new TypeError('webinar recordings latestCompletedAt must be a timestamp or null');
    }
    latestCompletedAt = value.latestCompletedAt;
  }
  return { count: value.count, latestCompletedAt };
}

/**
 * Narrows one catalog row. source must name the known vocabulary; a violating row raises
 * (the list parser fails loudly rather than guessing, mirroring the recordings client).
 */
export function parseWebinarEntry(value: unknown): WebinarEntry {
  if (!isRecord(value)) {
    throw new TypeError('webinar entry must be an object');
  }
  const room = parseWebinarRoom(value.room);
  const event = parseWebinarEvent(value.event);
  const live = parseWebinarLive(value.live);
  const recordings = parseWebinarRecordings(value.recordings);
  const registration = parseWebinarRegistration(value.registration);
  if (value.source !== 'calendar' && value.source !== 'adHoc') {
    throw new TypeError('webinar entry source must be "calendar" or "adHoc"');
  }
  return { event, room, live, recordings, registration, source: value.source };
}

/**
 * Narrows the catalog response body ({webinars: [...]}). The top level is strict; a malformed
 * row fails the whole parse (the composition is one server-side view - a half-true catalog
 * would be worse than a visible error).
 */
export function parseWebinars(body: unknown): readonly WebinarEntry[] {
  if (!isRecord(body) || !Array.isArray(body.webinars)) {
    throw new TypeError('webinars response must carry a "webinars" array');
  }
  return body.webinars.map((entry) => parseWebinarEntry(entry));
}

/**
 * GET /api/v1/webinars with the SSO bearer: the composed webinar catalog, server-sorted.
 */
export async function listWebinars(
  token: string,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<readonly WebinarEntry[]> {
  let response: Response;
  try {
    response = await fetchImpl('/api/v1/webinars', {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    throw new WebinarsApiError(0, 'network_error', 'control service is unreachable');
  }
  if (!response.ok) {
    let code = 'unknown_error';
    let message = `webinars request failed with HTTP ${String(response.status)}`;
    try {
      const body: unknown = await response.json();
      if (isRecord(body) && isRecord(body.error)) {
        if (typeof body.error.code === 'string') {
          code = body.error.code;
        }
        if (typeof body.error.message === 'string') {
          message = body.error.message;
        }
      }
    } catch {
      // non-JSON error body, keep the generic message
    }
    throw new WebinarsApiError(response.status, code, message);
  }
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new WebinarsApiError(response.status, 'invalid_response', 'response is not valid JSON');
  }
  try {
    return parseWebinars(body);
  } catch (cause: unknown) {
    const detail = cause instanceof TypeError ? cause.message : 'malformed webinars response';
    throw new WebinarsApiError(response.status, 'invalid_response', detail);
  }
}

/** The three dashboard sections, derived from the server-sorted listing. */
export interface WebinarSections {
  readonly live: readonly WebinarEntry[];
  readonly upcoming: readonly WebinarEntry[];
  readonly past: readonly WebinarEntry[];
}

/**
 * Seats left of a calendar row: null when the cap is unlimited or the row carries no
 * registration section (ad-hoc rows, module off) - "no limit" is not a number.
 */
export function webinarSeatsLeft(entry: WebinarEntry): number | null {
  if (entry.registration === null || entry.registration.capacity === null) {
    return null;
  }
  return Math.max(entry.registration.capacity - entry.registration.registered, 0);
}

/** A row is full when its registration section caps the roster and the roster reached it. */
export function webinarFull(entry: WebinarEntry): boolean {
  const seats = webinarSeatsLeft(entry);
  return seats !== null && seats === 0;
}

/** The SPA path of one webinar's detail page (the catalog cards deep-link here). */
export function webinarDetailPath(eventId: number): string {
  return `/webinars/${String(eventId)}`;
}

/**
 * Parses the {id} segment of a detail path: a positive integer, else null (the app shell
 * falls back to the catalog).
 */
export function parseWebinarDetailPath(pathname: string): number | null {
  const match = /^\/webinars\/(\d+)$/.exec(pathname);
  if (match === null) {
    return null;
  }
  const id = Number(match[1]);
  return Number.isInteger(id) && id > 0 ? id : null;
}

/**
 * Shared request plumbing of the registration calls (CR-27): bearer auth, the contract error
 * envelope, JSON responses. A 409 surfaces with its envelope code (already_registered /
 * capacity_full) so the UI can render the «Мест нет» state from the server's verdict.
 */
async function registrationRequest(
  token: string,
  eventId: number,
  method: 'POST' | 'GET',
  fetchImpl: typeof fetch,
): Promise<WebinarRegistration> {
  let response: Response;
  try {
    response = await fetchImpl(`/calendar/api/v1/events/${String(eventId)}/registration`, {
      method,
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    throw new WebinarsApiError(0, 'network_error', 'control service is unreachable');
  }
  if (!response.ok) {
    throw await registrationError(response);
  }
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new WebinarsApiError(response.status, 'invalid_response', 'response is not valid JSON');
  }
  try {
    if (!isRecord(body) || !isRecord(body.registration)) {
      throw new TypeError('registration response must carry a "registration" object');
    }
    const registration = parseWebinarRegistration(body.registration);
    if (registration === null) {
      throw new TypeError('registration section must not be null');
    }
    return registration;
  } catch (cause: unknown) {
    const detail = cause instanceof TypeError ? cause.message : 'malformed registration response';
    throw new WebinarsApiError(response.status, 'invalid_response', detail);
  }
}

/** Maps one non-2xx registration response onto the contract error. */
async function registrationError(response: Response): Promise<WebinarsApiError> {
  let code = 'unknown_error';
  let message = `registration request failed with HTTP ${String(response.status)}`;
  try {
    const body: unknown = await response.json();
    if (isRecord(body) && isRecord(body.error)) {
      if (typeof body.error.code === 'string') {
        code = body.error.code;
      }
      if (typeof body.error.message === 'string') {
        message = body.error.message;
      }
    }
  } catch {
    // non-JSON error body, keep the generic message
  }
  return new WebinarsApiError(response.status, code, message);
}

/**
 * POST /calendar/api/v1/events/{id}/registration (CR-27): puts the caller on the roster and
 * answers the fresh status shape. Throws WebinarsApiError with status 409 and code
 * capacity_full / already_registered on the conflict paths - the UI maps both onto the
 * «Мест нет» / «Вы записаны» states.
 */
export function registerForWebinar(
  token: string,
  eventId: number,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<WebinarRegistration> {
  return registrationRequest(token, eventId, 'POST', fetchImpl);
}

/**
 * DELETE /calendar/api/v1/events/{id}/registration (CR-27): removes the caller's roster row;
 * 204 carries no body, the caller refreshes the status read afterwards.
 */
export async function cancelWebinarRegistration(
  token: string,
  eventId: number,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<void> {
  let response: Response;
  try {
    response = await fetchImpl(`/calendar/api/v1/events/${String(eventId)}/registration`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    throw new WebinarsApiError(0, 'network_error', 'control service is unreachable');
  }
  if (response.status === 204) {
    return;
  }
  throw await registrationError(response);
}

/**
 * GET /calendar/api/v1/events/{id}/registration (CR-27): the caller's registration state plus
 * the roster size - the my-status read the detail page refreshes after every write.
 */
export function getWebinarRegistration(
  token: string,
  eventId: number,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<WebinarRegistration> {
  return registrationRequest(token, eventId, 'GET', fetchImpl);
}

/**
 * Splits the catalog into the page's sections. The server order already encodes the split:
 * a live row's window may be past (a session that ran late) or future (an early host), so
 * the section follows the presence, not the clock - live is live. Upcoming = the row's event
 * window still open; everything else (past events and idle ad-hoc rooms) is past.
 */
export function splitWebinarSections(
  entries: readonly WebinarEntry[],
  now: Date,
): WebinarSections {
  const live: WebinarEntry[] = [];
  const upcoming: WebinarEntry[] = [];
  const past: WebinarEntry[] = [];
  for (const entry of entries) {
    if (entry.live !== null) {
      live.push(entry);
      continue;
    }
    if (entry.event !== null && new Date(entry.event.endsAt).getTime() > now.getTime()) {
      upcoming.push(entry);
      continue;
    }
    past.push(entry);
  }
  return { live, upcoming, past };
}
